/**
 * Audit and provenance wrapper for Arrange engine.
 * Emits structured audit events for each tool run.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
  CanonicalScoreSet,
} from "../harmonizer/types";
import { runArrangeTool } from "./engine";

/**
 * Audit event types emitted by the Arrange engine.
 */
export type ArrangeAuditEvent =
  | {
      type: "ToolRunStarted";
      timestamp: number;
      sessionId: string;
      userId: string;
      toolName: string;
      criteriaSetId?: string | null;
      lensId?: string | null;
      toolConfig: ToolConfig;
      itemCount: number;
    }
  | {
      type: "ToolRunCompleted";
      timestamp: number;
      sessionId: string;
      userId: string;
      toolName: string;
      criteriaSetId?: string | null;
      lensId?: string | null;
      toolConfig: ToolConfig;
      itemCount: number;
      scoreSet: CanonicalScoreSet;
    }
  | {
      type: "ToolRunFailed";
      timestamp: number;
      sessionId: string;
      userId: string;
      toolName: string;
      criteriaSetId?: string | null;
      lensId?: string | null;
      toolConfig: ToolConfig;
      itemCount: number;
      error: string;
    }
  | {
      type: "PrioritySnapshotCreated";
      timestamp: number;
      sessionId: string;
      userId: string;
      toolName: string;
      criteriaSetId?: string | null;
      lensId?: string | null;
      scoreSet: CanonicalScoreSet;
    };

/**
 * Abstract sink interface for audit events.
 * Implementations can persist to database, log to file, send to analytics, etc.
 */
export interface ArrangeAuditSink {
  /**
   * Emits an audit event.
   * 
   * @param event - Audit event to emit
   */
  emit(event: ArrangeAuditEvent): Promise<void>;
}

/**
 * Runs an Arrange tool with audit event emission.
 * 
 * Wraps runArrangeTool and emits ToolRunStarted and ToolRunCompleted events.
 * On error, emits ToolRunFailed event and propagates the error.
 * 
 * @param sink - Audit sink to emit events to
 * @param sessionId - Session identifier
 * @param userId - User identifier
 * @param toolName - Name of the tool to run
 * @param items - Items to prioritize
 * @param criteriaSet - CriteriaSet for evaluation (null if not required)
 * @param toolConfig - Tool-specific configuration
 * @param lens - Optional Lens for filtering/sorting items
 * @returns CanonicalScoreSet with tool execution results
 */
export async function runArrangeToolWithAudit(
  sink: ArrangeAuditSink,
  sessionId: string,
  userId: string,
  toolName: string,
  items: Item[],
  criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens: Lens | null
): Promise<CanonicalScoreSet> {
  const timestamp = Date.now();
  const criteriaSetId = criteriaSet?.id || null;
  const lensId = lens?.id || null;

  // Emit ToolRunStarted event
  await sink.emit({
    type: "ToolRunStarted",
    timestamp,
    sessionId,
    userId,
    toolName,
    criteriaSetId,
    lensId,
    toolConfig,
    itemCount: items.length,
  });

  try {
    // Run the tool
    const scoreSet = await runArrangeTool(
      toolName,
      items,
      criteriaSet,
      toolConfig,
      lens
    );

    // Emit ToolRunCompleted event
    await sink.emit({
      type: "ToolRunCompleted",
      timestamp: Date.now(),
      sessionId,
      userId,
      toolName,
      criteriaSetId,
      lensId,
      toolConfig,
      itemCount: items.length,
      scoreSet,
    });

    return scoreSet;
  } catch (error) {
    // Emit ToolRunFailed event
    await sink.emit({
      type: "ToolRunFailed",
      timestamp: Date.now(),
      sessionId,
      userId,
      toolName,
      criteriaSetId,
      lensId,
      toolConfig,
      itemCount: items.length,
      error: error instanceof Error ? error.message : String(error),
    });

    // Propagate the error
    throw error;
  }
}

/**
 * Emits a PrioritySnapshotCreated audit event.
 * 
 * This event represents a finalized priority snapshot that can be used for
 * decision-making, reporting, or historical tracking.
 * 
 * @param sink - Audit sink to emit events to
 * @param sessionId - Session identifier
 * @param userId - User identifier
 * @param toolName - Name of the tool that generated the snapshot
 * @param scoreSet - Canonical score set representing the priority snapshot
 * @param criteriaSet - CriteriaSet used (null if not applicable)
 * @param lens - Lens used (null if not applicable)
 */
export async function emitPrioritySnapshot(
  sink: ArrangeAuditSink,
  sessionId: string,
  userId: string,
  toolName: string,
  scoreSet: CanonicalScoreSet,
  criteriaSet: CriteriaSet | null,
  lens: Lens | null
): Promise<void> {
  const timestamp = Date.now();
  const criteriaSetId = criteriaSet?.id || null;
  const lensId = lens?.id || null;

  await sink.emit({
    type: "PrioritySnapshotCreated",
    timestamp,
    sessionId,
    userId,
    toolName,
    criteriaSetId,
    lensId,
    scoreSet,
  });
}

/**
 * Simple console audit sink for development and testing.
 * Logs all events to the console.
 */
export class ConsoleAuditSink implements ArrangeAuditSink {
  async emit(event: ArrangeAuditEvent): Promise<void> {
    console.log("[ArrangeAudit]", JSON.stringify(event, null, 2));
  }
}

